import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Circle } from "lucide-react";

interface Vendor {
  id: string;
  name: string;
  status: "active" | "responded" | "inactive";
  lastBidAt: string | null;
}

interface ActiveVendorsPanelProps {
  rfpId: string;
  bids: Array<{
    proposal_id: string;
    created_at: string;
    status: string;
    proposal: { supplier_company_name: string };
  }>;
  proposals: Array<{
    id: string;
    supplier_company_name: string;
  }>;
}

// Consider a vendor "active" if they bid within the last 5 minutes
const ACTIVE_THRESHOLD_MS = 5 * 60 * 1000;

export function ActiveVendorsPanel({ rfpId, bids, proposals }: ActiveVendorsPanelProps) {
  const [vendors, setVendors] = useState<Vendor[]>([]);

  useEffect(() => {
    // Derive vendor status from proposals and bids
    const now = Date.now();
    const vendorMap = new Map<string, Vendor>();

    // Initialize all proposals as vendors
    proposals.forEach((proposal) => {
      vendorMap.set(proposal.id, {
        id: proposal.id,
        name: proposal.supplier_company_name,
        status: "responded", // They submitted a proposal
        lastBidAt: null,
      });
    });

    // Update based on bids
    bids.forEach((bid) => {
      const vendor = vendorMap.get(bid.proposal_id);
      if (vendor) {
        const bidTime = new Date(bid.created_at).getTime();
        const isRecent = now - bidTime < ACTIVE_THRESHOLD_MS;
        
        // Update to most recent bid time
        if (!vendor.lastBidAt || new Date(bid.created_at) > new Date(vendor.lastBidAt)) {
          vendor.lastBidAt = bid.created_at;
        }
        
        // Mark as active if bid is recent
        if (isRecent) {
          vendor.status = "active";
        }
      }
    });

    // Sort: active first, then by last bid time
    const sortedVendors = Array.from(vendorMap.values()).sort((a, b) => {
      if (a.status === "active" && b.status !== "active") return -1;
      if (b.status === "active" && a.status !== "active") return 1;
      if (a.lastBidAt && b.lastBidAt) {
        return new Date(b.lastBidAt).getTime() - new Date(a.lastBidAt).getTime();
      }
      return 0;
    });

    setVendors(sortedVendors);
  }, [bids, proposals]);

  const activeCount = vendors.filter((v) => v.status === "active").length;

  const getStatusIndicator = (status: Vendor["status"]) => {
    switch (status) {
      case "active":
        return (
          <div className="flex items-center gap-1.5">
            <Circle className="h-2 w-2 fill-green-500 text-green-500 animate-pulse" />
            <span className="text-xs text-green-600 font-medium">Active</span>
          </div>
        );
      case "responded":
        return (
          <div className="flex items-center gap-1.5">
            <Circle className="h-2 w-2 fill-blue-500 text-blue-500" />
            <span className="text-xs text-blue-600 font-medium">Responded</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5">
            <Circle className="h-2 w-2 fill-muted-foreground text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-medium">Inactive</span>
          </div>
        );
    }
  };

  if (vendors.length === 0) {
    return null;
  }

  return (
    <Card className="border border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Users className="h-4 w-4" />
            Active Vendors
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {activeCount} active
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {vendors.map((vendor) => (
            <div
              key={vendor.id}
              className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30"
            >
              <span className="text-sm font-medium text-foreground truncate max-w-[200px]">
                {vendor.name}
              </span>
              {getStatusIndicator(vendor.status)}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
